import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatCard, RagBadge, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { InspectorLinksDialog } from "@/components/InspectorLinksDialog";
import { ChampionBadge } from "@/components/ChampionBadge";
import { ManagerCard } from "@/components/ManagerCard";
import { Users, CheckCircle2, Share2, Printer, AlertTriangle, Award, Download, BookOpen, PlayCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import AdminOverview from "@/pages/dashboard/AdminOverview";
import TrainerSessions from "@/pages/dashboard/TrainerSessions";
import EvidenceChecklist from "@/pages/dashboard/EvidenceChecklist";


export default function DashboardHome() {
  const { user } = useAuth();
  if (user.role === "admin") return <AdminOverview />;
  if (user.role === "trainer") return <TrainerSessions />;
  if (user.role === "staff") return <StaffHome />;
  return <ManagerOverview />;
}

function ManagerOverview() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/compliance/summary").then((r) => setData(r.data)).catch(() => {}); }, []);

  const exportPack = async () => {
    try {
      const res = await api.get("/compliance/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "CQC-evidence-pack.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const downloadLeaflet = async () => {
    try {
      toast.info("Building your family leaflet…");
      const res = await api.get("/compliance/family-leaflet.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "Mouth-care-at-our-home.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Leaflet download failed"); }
  };
  const downloadPoster = useCallback(async () => {
    try {
      toast.info("Building your Trained Team poster…");
      const res = await api.get("/compliance/team-poster.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "Trained-Team-Poster.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Poster download failed"); }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("download") === "poster") {
      window.history.replaceState({}, "", window.location.pathname);
      downloadPoster();
    }
  }, [downloadPoster]);

  const exportPdf = async () => {
    try {
      toast.info("Building your evidence pack…");
      const res = await api.get("/compliance/export-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "CQC-Evidence-Pack.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Evidence pack PDF downloaded");
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <Loading />;
  return (
    <div>
      <PageHeader title="Oral Health Assurance" subtitle="A quarterly, risk-based review of safe, effective and person-centred oral healthcare — mapped to the CQC Single Assessment Framework.">
        <div className="flex flex-wrap gap-2">
          <Button data-testid="export-pdf-btn" onClick={exportPdf} className="rounded-full bg-brand hover:bg-brand-hover text-white"><Download className="h-4 w-4 mr-2" />Evidence pack (PDF)</Button>
          <Button data-testid="export-evidence-btn" variant="outline" onClick={exportPack} className="rounded-full border-brand text-brand hover:bg-brand hover:text-white">CSV</Button>
          <Button data-testid="share-inspector-btn" variant="outline" onClick={() => setShareOpen(true)} className="rounded-full border-brand text-brand hover:bg-brand hover:text-white"><Share2 className="h-4 w-4 mr-2" />Share with inspector</Button>
          <Button data-testid="team-poster-btn" variant="outline" onClick={downloadPoster} className="rounded-full border-brand text-brand hover:bg-brand hover:text-white"><Printer className="h-4 w-4 mr-2" />Team poster</Button>
          <Button data-testid="family-leaflet-btn" variant="outline" onClick={downloadLeaflet} className="rounded-full border-brand text-brand hover:bg-brand hover:text-white"><FileText className="h-4 w-4 mr-2" />Family leaflet</Button>
        </div>
      </PageHeader>
      <InspectorLinksDialog open={shareOpen} onOpenChange={setShareOpen} />
      <EvidenceChecklist training={data} trainingPanel={<StaffTrainingPanel data={data} onAssign={() => nav("/app/team")} />} />
    </div>
  );
}

function StaffTrainingPanel({ data, onAssign }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-staff" label="Team members" value={data.staff_count} icon={Users} />
        <StatCard testid="stat-completion" label="Completion" value={`${data.completion_pct}%`} icon={CheckCircle2} tone={data.completion_pct >= 90 ? "green" : data.completion_pct >= 75 ? "amber" : data.staff_count ? "red" : "brand"} />
        <StatCard testid="stat-overdue" label="Overdue" value={data.overdue} icon={AlertTriangle} tone={data.overdue ? "red" : "green"} />
        <StatCard testid="stat-certs" label="Certificates" value={data.certificates} icon={Award} tone="coral" />
      </div>
      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-cream-deep flex items-center justify-between">
          <h3 className="font-heading font-semibold text-brand">Staff training status</h3>
          <Button size="sm" variant="outline" onClick={onAssign} className="rounded-full">Assign courses</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Role</TableHead>
                <TableHead className="text-center">Assigned</TableHead><TableHead className="text-center">Completed</TableHead>
                <TableHead className="text-center">Overdue</TableHead><TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={r.user_id} data-testid={`compliance-row-${i}`} className={i % 2 ? "bg-cream/50" : ""}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.job_role}</TableCell>
                  <TableCell className="text-center">{r.assigned}</TableCell>
                  <TableCell className="text-center">{r.completed}</TableCell>
                  <TableCell className="text-center">{r.overdue > 0 ? <span className="text-rag-red font-semibold">{r.overdue}</span> : "—"}</TableCell>
                  <TableCell className="text-right"><RagBadge status={r.rag} /></TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No staff yet. Add your team to get started.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}


function StaffHome() {
  const nav = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  useEffect(() => { api.get("/enrollments/me").then((r) => setEnrollments(r.data)).catch(() => {}); }, []);

  const done = enrollments.filter((e) => e.status === "completed").length;
  return (
    <div>
      <PageHeader title="My learning" subtitle="Complete your assigned courses to earn certificates." />
      <ManagerCard />
      <ChampionBadge />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard testid="stat-assigned" label="Assigned" value={enrollments.length} icon={BookOpen} />
        <StatCard testid="stat-done" label="Completed" value={done} icon={CheckCircle2} tone="green" />
        <StatCard testid="stat-inprogress" label="In progress" value={enrollments.length - done} icon={PlayCircle} tone="coral" />
      </div>
      <div className="space-y-3">
        {enrollments.map((e) => (
          <div key={e.id} data-testid={`my-course-${e.course_id}`} className="bg-cream-card border border-cream-deep rounded-xl p-5 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-brand">{e.course_title}</div>
              <div className="flex items-center gap-3 mt-2 max-w-md">
                <Progress value={e.progress} className="h-2" />
                <span className="text-sm text-muted-foreground w-10">{e.progress}%</span>
              </div>
            </div>
            <Button data-testid={`continue-${e.course_id}`} onClick={() => nav(`/app/courses/${e.course_id}`)} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {e.status === "completed" ? "Review" : "Continue"}
            </Button>
          </div>
        ))}
        {enrollments.length === 0 && (
          <div className="text-center py-16 bg-cream-card border border-cream-deep rounded-xl">
            <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No courses assigned yet. Browse the library to start learning.</p>
            <Button onClick={() => nav("/app/courses")} className="mt-4 rounded-full bg-brand text-white">Browse courses</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Loading() {
  return <div className="grid place-items-center py-24 text-muted-foreground">Loading…</div>;
}
